import { useCallback, useEffect, useState } from "react";

import { isIos, isMobileNative } from "~/utils/helpers";
import { useMountEffect } from "~/hooks/useMountEffect";

import { isFullscreenSupported, isPipSupported } from "../../utils/features";
import { useMediaPlayerContext } from "../media/context";
import { MediaPlayerViewMode } from "../media/types";
import { VideoControl } from "./VideoControl";
import { VideoTimeline } from "./VideoTimeline";
import { VideoControlWindowPosition } from "./VideoControlWindowPosition";
import { VolumeControl } from "../media/VolumeControl";
import { PlaybackRateControl } from "../media/PlaybackRateControl";

import type { MediaPlayerFullscreenWindowPosition } from "../media/types";

export interface VideoControlsProps {
  onAirplayControlClick: () => void;
  onCaptionControlClick: () => void;
  onFullscreenControlClick: () => void;
  onGoBackwardControlClick: () => void;
  onGoForwardControlClick: () => void;
  onPipControlClick: () => void;
  onPlaybackControlSpeedChange: (rate: number) => void;
  onVolumeChange: (volume: number) => void;
  onPlayPauseControlClick: () => void;
  preventHidingControls: () => void;
}

export function VideoControls({
  onAirplayControlClick,
  onCaptionControlClick,
  onFullscreenControlClick,
  onGoBackwardControlClick,
  onGoForwardControlClick,
  onPipControlClick,
  onPlaybackControlSpeedChange,
  onVolumeChange,
  onPlayPauseControlClick,
  preventHidingControls,
}: VideoControlsProps) {
  const {
    viewMode,
    airplaying,
    canAirplay,
    captionsEnabled,
    fullscreen,
    viewModeFullscreenSlides,
    setViewModeFullscreenSlides,
    viewModeFullscreenWindowPosition,
    setViewModeFullscreenWindowPosition,
    paused,
    playbackRate,
    volume,
  } = useMediaPlayerContext();
  const [showPipControl, setShowPipControl] = useState(false);
  const [showFullscreenControl, setShowFullscreenControl] = useState(false);
  const [showVolumeControl, setShowVolumeControl] = useState(false);

  const handleViewModeFullscreenSlidesClick = useCallback(() => {
    setViewModeFullscreenSlides?.(!viewModeFullscreenSlides);
  }, [viewModeFullscreenSlides, setViewModeFullscreenSlides]);

  const handleWindowPositionChange = useCallback(
    (position: MediaPlayerFullscreenWindowPosition) => {
      setViewModeFullscreenWindowPosition?.(position);
    },
    [setViewModeFullscreenWindowPosition],
  );

  const showAirplayControl =
    canAirplay && viewMode !== MediaPlayerViewMode.fullscreen;

  useEffect(() => {
    // Defer testing pip capability until the client renders
    // to avoid a false-negative during server rendering.
    setShowPipControl(
      isPipSupported() &&
        !airplaying &&
        viewMode !== MediaPlayerViewMode.fullscreen,
    );
  }, [airplaying, viewMode]);

  useMountEffect(() => {
    setShowFullscreenControl(isFullscreenSupported() || isMobileNative());
    setShowVolumeControl(!isMobileNative() && !isIos());
  });

  return (
    <div className="VideoControls">
      <div className="VideoControls__utils">
        <VideoControl
          type="closed-captioning"
          isActive={captionsEnabled}
          onClick={onCaptionControlClick}
        />

        <PlaybackRateControl
          className="VideoControl"
          popoverPlacement="bottom"
          popoverOffset={-12}
          popoverStrategy="fixed" // Prevents being hidden by slides
          onActivate={preventHidingControls}
          value={playbackRate}
          onChange={onPlaybackControlSpeedChange}
        />

        {showVolumeControl && (
          <VolumeControl
            classes="VideoControl"
            popoverPlacement="bottom"
            popoverOffset={-12}
            popoverStrategy="fixed" // Prevents being hidden by slides
            onActivate={preventHidingControls}
            value={volume}
            onChange={onVolumeChange}
          />
        )}

        {showPipControl && (
          <VideoControl type="pip" onClick={onPipControlClick} />
        )}

        {fullscreen && (
          <>
            <VideoControl
              type="fullscreen-swap"
              onClick={handleViewModeFullscreenSlidesClick}
            />
            <VideoControlWindowPosition
              value={viewModeFullscreenWindowPosition}
              onChange={handleWindowPositionChange}
              onActivate={preventHidingControls}
            />
          </>
        )}

        {showAirplayControl && (
          <VideoControl
            type="airplay"
            isActive={airplaying}
            onClick={onAirplayControlClick}
          />
        )}

        {showFullscreenControl && (
          <VideoControl
            type="fullscreen"
            isActive={fullscreen}
            onClick={onFullscreenControlClick}
          />
        )}
      </div>

      <div className="VideoControls__playback">
        <VideoControl type={"go-backward"} onClick={onGoBackwardControlClick} />

        <VideoControl
          type="play-pause"
          isActive={paused}
          onClick={onPlayPauseControlClick}
        />

        <VideoControl type={"go-forward"} onClick={onGoForwardControlClick} />
      </div>

      <VideoTimeline />
    </div>
  );
}
